import React from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowRight, MapPin, Phone as PhoneIcon, Users, Globe, Building2 } from 'lucide-react';
import CurrencyTicker from './CurrencyTicker';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Rotterdam coordinates for weather
const RTM = { lat: 51.9225, lon: 4.4792 };
// Open-Meteo WMO weather code → icon + label
const wmo = (code) => {
  if (code === 0) return { icon: 'fa-sun', label: 'Clear', color: 'text-amber-500' };
  if (code <= 3) return { icon: 'fa-cloud-sun', label: 'Partly cloudy', color: 'text-slate-500' };
  if (code === 45 || code === 48) return { icon: 'fa-smog', label: 'Fog', color: 'text-slate-400' };
  if (code >= 51 && code <= 67) return { icon: 'fa-cloud-rain', label: 'Rain', color: 'text-sky-500' };
  if (code >= 71 && code <= 77) return { icon: 'fa-snowflake', label: 'Snow', color: 'text-sky-300' };
  if (code >= 80 && code <= 82) return { icon: 'fa-cloud-showers-heavy', label: 'Showers', color: 'text-sky-600' };
  if (code >= 95) return { icon: 'fa-bolt', label: 'Storm', color: 'text-purple-600' };
  return { icon: 'fa-cloud', label: 'Cloudy', color: 'text-slate-500' };
};

// Small live weather pill in the header — pulls Rotterdam temp from Open-Meteo (no API key)
const WeatherPill = () => {
  const [temp, setTemp] = React.useState(null);
  const [code, setCode] = React.useState(null);
  React.useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await fetch(`https://api.open-meteo.com/v1/forecast?latitude=${RTM.lat}&longitude=${RTM.lon}&current=temperature_2m,weather_code&timezone=Europe%2FAmsterdam`);
        const d = await r.json();
        if (!cancelled && d.current) {
          setTemp(Math.round(d.current.temperature_2m));
          setCode(d.current.weather_code);
        }
      } catch { /* silent — pill just hides */ }
    };
    load();
    const id = setInterval(load, 15 * 60 * 1000); // refresh every 15 min
    return () => { cancelled = true; clearInterval(id); };
  }, []);
  if (temp == null) return null;
  const w = wmo(code);
  return (
    <div className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 bg-white rounded-lg border border-slate-200 shadow-sm" data-testid="weather-pill" title={`${w.label} in Rotterdam`}>
      <i className={`fas ${w.icon} ${w.color} text-sm`}></i>
      <span className="text-xs font-bold text-slate-700 tabular-nums">{temp}°C</span>
      <span className="text-[9px] text-slate-400 font-semibold uppercase tracking-widest">Rtm</span>
    </div>
  );
};

// Live announcement banner controlled from admin — shows above the money-transfer circle
const AnnouncementBanner = () => {
  const [ann, setAnn] = React.useState(null);
  React.useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/announcement`);
        if (!cancelled) setAnn(r.data);
      } catch { /* silent */ }
    };
    load();
    const id = setInterval(load, 60 * 1000); // check every minute
    return () => { cancelled = true; clearInterval(id); };
  }, []);
  if (!ann || !ann.is_active) return null;
  const styles = {
    info: { bg: 'from-sky-500 to-blue-600', ring: 'ring-sky-500/40', icon: 'fa-circle-info', label: 'Notice' },
    sale: { bg: 'from-emerald-500 to-teal-600', ring: 'ring-emerald-500/40', icon: 'fa-tags', label: 'Special offer' },
    closed: { bg: 'from-red-500 to-rose-600', ring: 'ring-red-500/40', icon: 'fa-lock', label: 'Closed' },
    warning: { bg: 'from-amber-500 to-orange-600', ring: 'ring-amber-500/40', icon: 'fa-triangle-exclamation', label: 'Important' },
  };
  const s = styles[ann.kind] || styles.info;
  return (
    <div className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${s.bg} text-white p-5 shadow-xl ring-4 ${s.ring} w-full`} data-testid="home-announcement">
      <div className="absolute -top-6 -right-6 w-32 h-32 bg-white/10 rounded-full blur-2xl pointer-events-none"></div>
      <div className="absolute -bottom-6 -left-6 w-24 h-24 bg-black/10 rounded-full blur-2xl pointer-events-none"></div>
      <div className="flex items-start gap-4 relative">
        <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
          <i className={`fas ${s.icon} text-white text-lg`}></i>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-white/85">{s.label}</p>
          {ann.title && <p className="font-black text-lg leading-tight mt-1">{ann.title}</p>}
          {ann.message && <p className="text-sm text-white/95 mt-1.5 leading-relaxed break-words">{ann.message}</p>}
        </div>
      </div>
    </div>
  );
};

// Public suggestion modal — customers can leave feedback without an account
const SuggestionModal = ({ onClose }) => {
  const [name, setName] = React.useState('');
  const [contact, setContact] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [sending, setSending] = React.useState(false);
  const submit = async () => {
    if (message.trim().length < 3) {
      toast.error('Please write a suggestion (a few words are fine)');
      return;
    }
    setSending(true);
    try {
      const payload = { name: name.trim() || undefined, message: message.trim() };
      if (contact.trim()) {
        if (contact.includes('@')) payload.email = contact.trim();
        else payload.phone = contact.trim();
      }
      await axios.post(`${API}/suggestions`, payload);
      toast.success('Thank you — your suggestion was received!');
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send. Please try again.');
    } finally {
      setSending(false);
    }
  };
  return (
    <div className="fixed inset-0 z-[200] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="suggestion-modal">
      <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="inline-flex items-center gap-2 mb-2">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                <i className="fas fa-lightbulb text-emerald-600"></i>
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-600">Suggestions</p>
                <h3 className="font-black text-slate-900 text-lg leading-tight">Tell us what you think</h3>
              </div>
            </div>
            <p className="text-sm text-slate-500">We read every message. Ideas, complaints, requests — all welcome.</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="suggestion-close">
            <i className="fas fa-times text-lg"></i>
          </button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1 block">Your name <span className="text-slate-300 normal-case">(optional)</span></label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Sarah" className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:border-emerald-500 focus:outline-none" data-testid="suggestion-name" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1 block">Phone or Email <span className="text-slate-300 normal-case">(optional, if you want a reply)</span></label>
            <input type="text" value={contact} onChange={(e) => setContact(e.target.value)} placeholder="+31 6 … or you@email.com" className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:border-emerald-500 focus:outline-none" data-testid="suggestion-contact" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1 block">Your suggestion *</label>
            <textarea rows={4} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Please open on Sundays / You should sell iPhone cases / etc." maxLength={2000} className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:border-emerald-500 focus:outline-none resize-none" data-testid="suggestion-message" />
            <p className="text-[10px] text-slate-400 mt-1 text-right">{message.length}/2000</p>
          </div>
          <button onClick={submit} disabled={sending} className="w-full py-3 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-bold rounded-xl disabled:opacity-60 transition-all shadow-lg" data-testid="suggestion-submit">
            {sending ? <><i className="fas fa-spinner fa-spin mr-2"></i>Sending...</> : <><i className="fas fa-paper-plane mr-2"></i>Send suggestion</>}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// BragTiles — three "What we do" tiles with rotating product/service imagery.
// Tile 1: AI Package Pickup (brag), Tile 2: Cell Phones, Tile 3: Accessories.
// Images rotate every ~3s per tile using CSS crossfade.
// ============================================================================

const TILE_DATA = [
  {
    kind: 'ai',
    eyebrow: 'AI-powered',
    title: 'AI Package Pickup',
    titleDutch: 'AI Pakketafhaling',
    shortDesc: 'Scan, match & handover in seconds — no more waiting in line.',
    shortDescDutch: 'Scan, match & afhalen in seconden.',
    accent: 'from-sky-500 via-indigo-500 to-violet-600',
    ring: 'ring-sky-500/30',
    badge: 'FIRST IN ROTTERDAM',
    icon: 'fa-robot',
    images: [
      { src: 'https://customer-assets-rejwkqb3.emergentagent.net/job_18b15814-54ff-4979-8801-16c21d1a364b/artifacts/0ac8u9lc_a51052a0-5008-493e-9da1-13a20e21f04b.JPG', label: 'A-Shop Mobiles' },
      { src: 'https://images.unsplash.com/photo-1587293852726-70cdb56c2866?w=900&q=80&auto=format&fit=crop', label: 'Every parcel scanned' },
      { src: 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=900&q=80&auto=format&fit=crop', label: 'AI-powered' },
      { src: 'https://images.unsplash.com/photo-1595246140625-573b715d11dc?w=900&q=80&auto=format&fit=crop', label: 'Ready for pickup' },
    ],
    overlayCaptions: [
      { line: 'Instant barcode & label OCR', icon: 'fa-bolt' },
      { line: 'Auto WhatsApp notifications', icon: 'fa-comment-dots' },
      { line: 'ID-matched secure handover', icon: 'fa-shield-halved' },
      { line: '10× faster than manual pickup', icon: 'fa-gauge-high' },
    ],
  },
  {
    kind: 'phones',
    eyebrow: 'In store now',
    title: 'Cell Phones',
    titleDutch: 'Mobiele telefoons',
    shortDesc: 'Latest Redmi & Samsung models — unlocked, ready to go.',
    shortDescDutch: 'Nieuwste Redmi & Samsung modellen.',
    accent: 'from-emerald-500 via-teal-500 to-cyan-500',
    ring: 'ring-emerald-500/30',
    badge: 'NEW ARRIVALS',
    icon: 'fa-mobile-screen-button',
    images: [
      { src: 'https://images.unsplash.com/photo-1610945265064-0e34e5519bbf?w=800&q=80&auto=format&fit=crop', label: 'Samsung Galaxy S' },
      { src: 'https://images.unsplash.com/photo-1598327105666-5b89351aff97?w=800&q=80&auto=format&fit=crop', label: 'Redmi Note' },
      { src: 'https://images.unsplash.com/photo-1610792516307-ea5acd9c3b00?w=800&q=80&auto=format&fit=crop', label: 'Samsung Galaxy' },
      { src: 'https://images.unsplash.com/photo-1618478594486-c65b899c4936?w=800&q=80&auto=format&fit=crop', label: 'Galaxy Note' },
    ],
  },
  {
    kind: 'accessories',
    eyebrow: 'Grab & go',
    title: 'Phone Accessories',
    titleDutch: 'Telefoonaccessoires',
    shortDesc: 'Earbuds, headphones, chargers, cables — everything you need.',
    shortDescDutch: 'Oordopjes, koptelefoons, opladers.',
    accent: 'from-orange-500 via-pink-500 to-rose-500',
    ring: 'ring-pink-500/30',
    badge: 'FROM €5',
    icon: 'fa-headphones',
    images: [
      { src: 'https://images.unsplash.com/photo-1600294037681-c80b4cb5b434?w=800&q=80&auto=format&fit=crop', label: 'AirPods' },
      { src: 'https://images.unsplash.com/photo-1590658268037-6bf12165a8df?w=800&q=80&auto=format&fit=crop', label: 'Galaxy Buds' },
      { src: 'https://images.unsplash.com/photo-1583394838336-acd977736f90?w=800&q=80&auto=format&fit=crop', label: 'Headphones' },
      { src: 'https://images.unsplash.com/photo-1583863788434-e58a36330cf0?w=800&q=80&auto=format&fit=crop', label: 'Fast charger' },
    ],
  },
];

const BragTile = ({ tile }) => {
  const items = tile.images || [];
  const captions = tile.overlayCaptions || [];
  const [idx, setIdx] = React.useState(0);
  const [capIdx, setCapIdx] = React.useState(0);
  const [broken, setBroken] = React.useState({}); // src -> true if failed to load

  React.useEffect(() => {
    if (items.length <= 1) return;
    const id = setInterval(() => setIdx((i) => (i + 1) % items.length), 3000);
    return () => clearInterval(id);
  }, [items.length]);

  React.useEffect(() => {
    if (captions.length <= 1) return;
    const id = setInterval(() => setCapIdx((i) => (i + 1) % captions.length), 2400);
    return () => clearInterval(id);
  }, [captions.length]);

  const current = items[idx];
  const currentCap = captions[capIdx];
  const isBroken = current && broken[current.src];

  return (
    <div
      className={`group relative rounded-3xl bg-gradient-to-br ${tile.accent} p-[1.5px] shadow-2xl hover:-translate-y-1 transition-all duration-500 ring-4 ${tile.ring}`}
      data-testid={`brag-tile-${tile.kind}`}
    >
      <div className="relative rounded-[calc(1.5rem-1.5px)] bg-white h-full overflow-hidden">
        {/* Image / brag area */}
        <div className={`relative h-56 sm:h-60 overflow-hidden bg-gradient-to-br ${tile.accent}`}>
          {items.map((im, i) => (
            !broken[im.src] && (
              <img
                key={im.src}
                src={im.src}
                alt={im.label}
                loading="lazy"
                onError={() => setBroken((b) => ({ ...b, [im.src]: true }))}
                className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700"
                style={{ opacity: i === idx ? 1 : 0 }}
                draggable="false"
              />
            )
          ))}
          {/* Fallback icon shown if the currently-visible image failed to load */}
          {isBroken && (
            <div className="absolute inset-0 flex items-center justify-center text-white">
              <i className={`fas ${tile.icon} text-6xl opacity-80 drop-shadow-2xl`}></i>
            </div>
          )}
          {/* Dark gradient for legibility */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-black/20 pointer-events-none"></div>

          {/* Rotating brag caption for the AI tile (or any tile with overlayCaptions) */}
          {captions.length > 0 && (
            <div className="absolute top-1/2 left-4 right-4 -translate-y-1/2 flex justify-center pointer-events-none">
              <div
                key={capIdx}
                className="inline-flex items-center gap-2 px-4 py-2 bg-black/50 backdrop-blur-md text-white text-sm font-bold rounded-full ring-1 ring-white/20 shadow-2xl"
                style={{ animation: 'bragFade 0.6s ease-out' }}
                data-testid={`brag-caption-${tile.kind}`}
              >
                <i className={`fas ${currentCap?.icon} text-amber-300`}></i>
                <span>{currentCap?.line}</span>
              </div>
            </div>
          )}

          {/* Label + dots */}
          <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between text-white text-xs font-bold uppercase tracking-widest">
            <span className="drop-shadow">{current?.label}</span>
            <span className="flex items-center gap-1">
              {items.map((_, i) => (
                <span key={i} className={`h-1 rounded-full transition-all ${i === idx ? 'w-5 bg-white' : 'w-1.5 bg-white/40'}`}></span>
              ))}
            </span>
          </div>

          {/* Corner badge */}
          <span className="absolute top-3 left-3 px-2.5 py-1 bg-white/95 backdrop-blur text-slate-900 text-[10px] font-black tracking-widest rounded-full shadow-lg">
            {tile.badge}
          </span>
        </div>

        {/* Text area */}
        <div className="p-6">
          <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-slate-500">{tile.eyebrow}</p>
          <h3 className="mt-2 text-2xl font-bold tracking-tight text-slate-900">{tile.title}</h3>
          <p className="mt-0.5 text-sm text-slate-500 italic">{tile.titleDutch}</p>
          <p className="mt-3 text-slate-700 font-medium leading-snug">{tile.shortDesc}</p>
          <p className="mt-1 text-xs text-slate-500">{tile.shortDescDutch}</p>
        </div>
      </div>
    </div>
  );
};

const BragTiles = () => (
  <>
    {TILE_DATA.map((t) => <BragTile key={t.kind} tile={t} />)}
    <style>{`
      @keyframes bragFade {
        from { opacity: 0; transform: translateY(8px) scale(0.96); }
        to { opacity: 1; transform: translateY(0) scale(1); }
      }
    `}</style>
  </>
);


const GREETINGS = [
  { text: 'Welkom', lang: 'Dutch', flag: '🇳🇱' },
  { text: 'Welcome', lang: 'English', flag: '🇬🇧' },
  { text: 'Assalamu Alaikum', lang: 'Urdu', flag: '🇵🇰' },
  { text: 'Namaste', lang: 'Hindi', flag: '🇮🇳' },
  { text: 'হ্যালো', lang: 'Bangla', flag: '🇧🇩' },
  { text: 'Merhaba', lang: 'Turkish', flag: '🇹🇷' },
  { text: 'أهلا وسهلا', lang: 'Arabic', flag: '🇲🇦' },
  { text: 'Witaj', lang: 'Polish', flag: '🇵🇱' },
  { text: 'Kumusta', lang: 'Filipino', flag: '🇵🇭' },
  { text: 'Ayubowan', lang: 'Sinhala', flag: '🇱🇰' },
];

// Items that rotate in the right-hand hero circle.
// Money-transfer partners only — pops up one at a time like the language greeting.
const SHOWCASE = [
  {
    name: 'Western Union',
    caption: 'Send to 200+ countries',
    src: 'https://cdn.simpleicons.org/westernunion/000000',
    bg: 'from-yellow-300 via-yellow-400 to-amber-500',
    ring: 'ring-yellow-400/40',
    fit: 'contain',
  },
  {
    name: 'Ria',
    caption: 'Fast & secure',
    bg: 'from-orange-400 via-red-500 to-red-600',
    ring: 'ring-red-500/40',
    wordmark: { text: 'Ria', color: '#fff', italic: true, weight: 900, size: 60 },
  },
  {
    name: 'Fidur',
    caption: 'Local & trusted · Rotterdam',
    bg: 'from-sky-500 via-blue-600 to-indigo-700',
    ring: 'ring-blue-500/40',
    wordmark: { text: 'Fidur', color: '#fff', italic: false, weight: 800, size: 42 },
  },
];

const ShowcaseCarousel = () => {
  const [idx, setIdx] = React.useState(0);
  React.useEffect(() => {
    const id = setInterval(() => setIdx((i) => (i + 1) % SHOWCASE.length), 2600);
    return () => clearInterval(id);
  }, []);
  const item = SHOWCASE[idx];
  const total = SHOWCASE.length;

  return (
    <div className="w-full flex flex-col items-center justify-center" data-testid="showcase-carousel">
      {/* Tag above the circle */}
      <p className="text-[10px] font-bold uppercase tracking-[0.35em] text-sky-600 mb-4">
        <span className="inline-block w-6 h-px bg-current align-middle mr-2"></span>
        Money transfer partners
      </p>

      {/* Circular showcase */}
      <div className="relative w-[140px] h-[140px] sm:w-[170px] sm:h-[170px] lg:w-[200px] lg:h-[200px]">
        {/* Ambient glow behind the circle */}
        <div className={`absolute inset-0 rounded-full bg-gradient-to-br ${item.bg} blur-3xl opacity-40 transition-all duration-700`}></div>

        {/* The circle itself */}
        <div
          key={idx}
          className={`relative w-full h-full rounded-full bg-gradient-to-br ${item.bg} shadow-2xl ring-8 ${item.ring} overflow-hidden`}
          style={{ animation: 'circlePop 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)' }}
          data-testid={`showcase-item-${item.name.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
        >
          {/* Glossy highlight */}
          <div className="absolute top-4 left-6 w-1/2 h-1/3 bg-white/30 blur-2xl rounded-full pointer-events-none"></div>
          <div className="absolute -bottom-4 -right-4 w-1/2 h-1/2 bg-black/15 blur-3xl rounded-full pointer-events-none"></div>

          {/* Logo / wordmark */}
          <div className="relative h-full flex items-center justify-center px-10">
            {item.src && (
              <img
                src={item.src}
                alt={item.name}
                className="max-h-[45%] max-w-[70%] object-contain drop-shadow-2xl"
                draggable="false"
              />
            )}
            {item.wordmark && (
              <span
                className="drop-shadow-2xl"
                style={{
                  color: item.wordmark.color,
                  fontWeight: item.wordmark.weight,
                  fontStyle: item.wordmark.italic ? 'italic' : 'normal',
                  fontSize: `${item.wordmark.size}px`,
                  letterSpacing: '-0.03em',
                  lineHeight: 1,
                }}
              >
                {item.wordmark.text}
              </span>
            )}
          </div>
        </div>

        {/* Floating dot indicators around the bottom */}
        <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2">
          {SHOWCASE.map((_, i) => (
            <span
              key={i}
              className={`h-2 rounded-full transition-all duration-300 ${i === idx ? 'w-8 bg-slate-900' : 'w-2 bg-slate-300'}`}
            ></span>
          ))}
        </div>
      </div>

      {/* Name + caption below the circle */}
      <div
        className="mt-10 text-center"
        key={`meta-${idx}`}
        style={{ animation: 'showcaseFade 0.5s ease-out' }}
      >
        <p className="text-3xl font-bold tracking-tight text-slate-900">{item.name}</p>
        <p className="text-sm text-slate-500 mt-1">{item.caption}</p>
        <p className="text-[10px] text-slate-400 mt-2 font-mono uppercase tracking-widest">
          {String(idx + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}
        </p>
      </div>

      <style>{`
        @keyframes circlePop {
          0% { opacity: 0; transform: scale(0.85); }
          60% { opacity: 1; transform: scale(1.03); }
          100% { opacity: 1; transform: scale(1); }
        }
        @keyframes showcaseFade {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

// ============================================================================
// Rewards CTA banner — sits in the hero showcase column, drives customers to
// their PWA-installable wallet. Shows "Install to home screen" if the browser
// exposes the BeforeInstallPrompt event (Android Chrome / Edge).
// ============================================================================

const RewardsBanner = () => {
  const [installPrompt, setInstallPrompt] = React.useState(null);
  const [installed, setInstalled] = React.useState(false);

  React.useEffect(() => {
    const onPrompt = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };
    const onInstalled = () => {
      setInstallPrompt(null);
      setInstalled(true);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    // If already in standalone mode, consider it installed
    if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) {
      setInstalled(true);
    }
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const triggerInstall = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    if (outcome === 'accepted') setInstalled(true);
    setInstallPrompt(null);
  };

  return (
    <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-amber-400 via-orange-500 to-rose-500 text-white shadow-xl p-5" data-testid="rewards-banner">
      <div className="absolute -top-8 -right-8 w-40 h-40 bg-white/15 rounded-full blur-2xl"></div>
      <div className="relative flex items-center gap-4">
        <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center text-3xl shrink-0">
          🎁
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-black uppercase tracking-[0.3em] text-white/80">A-Shop Rewards</p>
          <h3 className="text-lg font-black leading-tight mt-0.5">Earn points on every purchase</h3>
          <p className="text-xs text-white/85 mt-1">No signup. Just show your QR at the counter.</p>
        </div>
      </div>
      <div className="relative mt-4 flex flex-wrap gap-2">
        <Link
          to="/wallet"
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-white text-slate-900 font-black rounded-xl text-sm shadow-lg hover:shadow-xl transition"
          data-testid="rewards-open-btn"
        >
          <i className="fas fa-wallet"></i> Open my wallet
        </Link>
        {installPrompt && !installed && (
          <button
            onClick={triggerInstall}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-black/25 hover:bg-black/40 text-white font-bold rounded-xl text-sm backdrop-blur"
            data-testid="pwa-install-btn"
          >
            <i className="fas fa-mobile-screen"></i> Add to home screen
          </button>
        )}
        {installed && (
          <span className="inline-flex items-center gap-2 px-3 py-2 text-xs font-bold text-white/90 bg-emerald-500/30 rounded-xl">
            <i className="fas fa-circle-check"></i> Installed
          </span>
        )}
      </div>
    </div>
  );
};


const Home = () => {
  const [greetingIdx, setGreetingIdx] = React.useState(0);
  const [showSuggestion, setShowSuggestion] = React.useState(false);

  React.useEffect(() => {
    const id = setInterval(() => setGreetingIdx((i) => (i + 1) % GREETINGS.length), 2200);
    return () => clearInterval(id);
  }, []);

  const currentGreeting = GREETINGS[greetingIdx];

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-amber-50 text-slate-900" data-testid="home-page">
      {/* Currency Ticker (stays dark for premium feel) */}
      <CurrencyTicker />

      {/* Sticky glass header */}
      <header className="sticky top-0 z-50 bg-white/70 backdrop-blur-xl border-b border-slate-200/60">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3" data-testid="home-brand">
            <div className="w-9 h-9 rounded-lg bg-sky-500/10 border border-sky-500/20 flex items-center justify-center">
              <i className="fas fa-store text-sky-600"></i>
            </div>
            <div className="leading-none">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Ashop</p>
              <p className="font-semibold tracking-tight text-slate-900">Rotterdam</p>
            </div>
          </div>
          <nav className="flex items-center gap-2 text-sm font-medium">
            <WeatherPill />
            <Link to="/" className="px-3 py-1.5 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors" data-testid="nav-home">Home</Link>
            <Link to="/contact" className="px-3 py-1.5 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors" data-testid="nav-contact">Contact</Link>
            <Link to="/wallet" className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 text-amber-700 hover:text-amber-900 hover:bg-amber-50 rounded-lg transition-colors font-semibold" data-testid="nav-rewards">
              <i className="fas fa-gift"></i> Rewards
            </Link>
            <button
              onClick={() => setShowSuggestion(true)}
              className="px-3 py-1.5 text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50 rounded-lg transition-colors font-semibold"
              data-testid="nav-suggestion-btn"
            >
              <i className="fas fa-lightbulb mr-1"></i> Suggestions
            </button>
            <Link to="/admin-login" className="px-3 py-1.5 bg-slate-900 text-white hover:bg-slate-800 rounded-lg transition-colors" data-testid="nav-login">Login</Link>
          </nav>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden">
        {/* Warm ambient light — welcoming, multi-community feel */}
        <div className="absolute -top-24 -left-24 w-[520px] h-[520px] bg-sky-300/40 rounded-full blur-3xl pointer-events-none animate-pulse" style={{ animationDuration: '8s' }}></div>
        <div className="absolute top-32 right-0 w-[420px] h-[420px] bg-amber-200/50 rounded-full blur-3xl pointer-events-none animate-pulse" style={{ animationDuration: '10s', animationDelay: '1s' }}></div>
        <div className="absolute top-96 left-1/3 w-[380px] h-[380px] bg-rose-200/40 rounded-full blur-3xl pointer-events-none animate-pulse" style={{ animationDuration: '12s', animationDelay: '2s' }}></div>
        <div className="absolute -bottom-20 right-1/4 w-[440px] h-[440px] bg-emerald-200/30 rounded-full blur-3xl pointer-events-none animate-pulse" style={{ animationDuration: '14s', animationDelay: '3s' }}></div>

        {/* Subtle dot grid pattern (nods to the global community you serve) */}
        <div
          className="absolute inset-0 opacity-[0.15] pointer-events-none"
          style={{
            backgroundImage: `radial-gradient(circle, rgba(15, 23, 42, 0.4) 1px, transparent 1px)`,
            backgroundSize: '32px 32px',
            maskImage: 'radial-gradient(ellipse at center, black 30%, transparent 80%)',
            WebkitMaskImage: 'radial-gradient(ellipse at center, black 30%, transparent 80%)',
          }}
        ></div>

        {/* Rotterdam skyline silhouette */}
        <div className="absolute bottom-0 left-0 right-0 h-56 pointer-events-none overflow-hidden">
          <svg viewBox="0 0 1400 220" preserveAspectRatio="xMidYEnd slice" className="absolute bottom-0 left-0 w-full h-full" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" data-testid="rotterdam-skyline">
            <defs>
              <linearGradient id="waterGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#0ea5e9" stopOpacity="0.15" />
                <stop offset="100%" stopColor="#0284c7" stopOpacity="0.35" />
              </linearGradient>
            </defs>
            <path d="M 0 180 Q 200 150, 400 165 T 800 155 T 1200 165 T 1400 160 L 1400 220 L 0 220 Z" fill="#94a3b8" opacity="0.15" />
            <g fill="#64748b" opacity="0.35">
              <rect x="60" y="140" width="30" height="60" /><rect x="100" y="120" width="25" height="80" /><rect x="135" y="150" width="35" height="50" /><rect x="180" y="125" width="28" height="75" /><rect x="230" y="135" width="40" height="65" />
              <rect x="1100" y="130" width="35" height="70" /><rect x="1145" y="115" width="30" height="85" /><rect x="1185" y="140" width="40" height="60" /><rect x="1235" y="125" width="28" height="75" /><rect x="1275" y="135" width="35" height="65" /><rect x="1320" y="120" width="30" height="80" />
            </g>
            <g fill="#475569" opacity="0.55">
              <path d="M 290 200 L 290 130 Q 340 90, 390 130 L 390 200 Z" />
              <rect x="295" y="140" width="90" height="60" fill="#334155" opacity="0.6" />
            </g>
            <g fill="#f59e0b" opacity="0.65">
              <polygon points="440,160 470,140 500,160 470,180" /><polygon points="475,175 505,155 535,175 505,195" /><polygon points="510,160 540,140 570,160 540,180" /><polygon points="545,175 575,155 605,175 575,195" />
            </g>
            <rect x="435" y="180" width="180" height="20" fill="#334155" opacity="0.5" />
            <rect x="0" y="200" width="1400" height="20" fill="url(#waterGradient)" />
            <g stroke="#0369a1" strokeWidth="2.5" fill="none" opacity="0.8">
              <line x1="820" y1="200" x2="820" y2="40" strokeWidth="4" />
              <line x1="820" y1="40" x2="750" y2="90" strokeWidth="3" />
              <line x1="820" y1="60" x2="670" y2="200" /><line x1="820" y1="75" x2="710" y2="200" /><line x1="820" y1="90" x2="750" y2="200" /><line x1="820" y1="105" x2="790" y2="200" />
              <line x1="820" y1="60" x2="970" y2="200" /><line x1="820" y1="75" x2="930" y2="200" /><line x1="820" y1="90" x2="890" y2="200" /><line x1="820" y1="105" x2="850" y2="200" />
              <line x1="640" y1="200" x2="1000" y2="200" strokeWidth="4" />
            </g>
            <g fill="#475569" opacity="0.6">
              <rect x="1010" y="100" width="45" height="100" /><rect x="1060" y="80" width="50" height="120" /><rect x="1115" y="110" width="40" height="90" />
            </g>
            <g stroke="#334155" strokeWidth="2" fill="#334155" opacity="0.5">
              <polygon points="20,200 20,140 40,140 40,200" /><circle cx="30" cy="140" r="3" />
              <line x1="30" y1="140" x2="8" y2="118" strokeWidth="3" /><line x1="30" y1="140" x2="52" y2="118" strokeWidth="3" /><line x1="30" y1="140" x2="8" y2="162" strokeWidth="3" /><line x1="30" y1="140" x2="52" y2="162" strokeWidth="3" />
            </g>
            <text x="700" y="215" textAnchor="middle" fill="#0369a1" opacity="0.4" fontSize="10" fontFamily="IBM Plex Mono, monospace" letterSpacing="6">ROTTERDAM · 51.9225° N, 4.4792° E</text>
          </svg>
        </div>


        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-16 lg:pt-24 pb-32">
          <div className="grid md:grid-cols-[1.15fr_1fr] gap-10 lg:gap-16 items-center">
          <div>
          <div className="max-w-2xl">
            {/* Rotating multi-language greeting */}
            <div className="inline-flex items-center gap-3 mb-4 min-h-[40px]" data-testid="rotating-greeting" key={greetingIdx}>
              <span className="text-3xl leading-none">{currentGreeting.flag}</span>
              <div className="leading-none">
                <p className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" style={{ animation: 'fadeIn 0.4s ease-out' }}>
                  {currentGreeting.text}
                </p>
                <p className="text-xs text-slate-500 mt-1">{currentGreeting.lang}</p>
              </div>
            </div>
            <h1 className="mt-6 text-5xl lg:text-7xl font-bold tracking-tight leading-[1.05] text-slate-900" data-testid="hero-title">
              Send money.<br />
              Get your parcel.<br />
              <span className="text-sky-600">Print anything.</span>
            </h1>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-xl">
              Your one-stop shop on Schiedamseweg. Money transfer, package pickup, printing, phones and everyday essentials — all under one roof.
            </p>
          </div>

          {/* Compact pill buttons */}
          <div className="mt-10 flex flex-wrap items-center gap-3" data-testid="home-cta-grid">
            <Link
              to="/customer"
              data-testid="home-track-btn"
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-green-500 via-blue-500 to-purple-600 text-white font-bold rounded-2xl hover:from-green-600 hover:via-blue-600 hover:to-purple-700 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              <i className="fas fa-search"></i>
              <span>Track Package</span>
              <i className="fas fa-arrow-right text-sm"></i>
            </Link>
            <Link
              to="/print"
              data-testid="home-print-btn"
              className="relative inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-yellow-500 via-orange-500 to-red-500 text-white font-bold rounded-2xl hover:from-yellow-600 hover:via-orange-600 hover:to-red-600 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              <i className="fas fa-print"></i>
              <span>Print</span>
              <span className="absolute -top-2 -right-2 bg-emerald-400 text-emerald-900 text-[10px] font-black px-2 py-0.5 rounded-full uppercase tracking-wider shadow">New</span>
            </Link>
            <Link
              to="/shop"
              data-testid="home-shop-btn"
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 text-white font-bold rounded-2xl hover:from-emerald-600 hover:via-teal-600 hover:to-cyan-600 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              <i className="fas fa-store"></i>
              <span>Shop</span>
            </Link>
            <Link
              to="/app"
              data-testid="home-app-btn"
              className="relative inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-pink-500 via-fuchsia-500 to-violet-600 text-white font-bold rounded-2xl hover:from-pink-600 hover:via-fuchsia-600 hover:to-violet-700 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              <i className="fas fa-mobile-screen-button"></i>
              <span>App</span>
              <span className="absolute -top-2 -right-2 bg-yellow-400 text-yellow-900 text-[10px] font-black px-2 py-0.5 rounded-full uppercase tracking-wider shadow animate-pulse">Soon</span>
            </Link>
          </div>
          {/* Languages we speak — small warm touch */}
          <div className="mt-8 flex items-center gap-3 flex-wrap" data-testid="languages-strip">
            <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">We understand</span>
            <div className="flex items-center gap-1.5 flex-wrap">
              {['🇳🇱 Dutch', '🇬🇧 English', '🇵🇰 اردو', '🇮🇳 हिन्दी', '🇧🇩 বাংলা', '🇹🇷 Türkçe', '🇲🇦 عربي'].map((l) => (
                <span key={l} className="text-xs px-2.5 py-1 bg-white border border-slate-200 rounded-full text-slate-700 font-medium shadow-sm">
                  {l}
                </span>
              ))}
            </div>
          </div>
          </div>

          {/* RIGHT-SIDE SHOWCASE — money-transfer circle on top, announcement banner below */}
          <div className="relative z-10 flex flex-col items-stretch justify-center" data-testid="hero-showcase">
            <div className="flex items-start justify-center">
              <ShowcaseCarousel />
            </div>
            <div className="mt-6">
              <AnnouncementBanner />
            </div>
            <div className="mt-4">
              <RewardsBanner />
            </div>
          </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="relative py-24 bg-white overflow-hidden">
        {/* Warm decorative orbs */}
        <div className="absolute top-10 -right-32 w-96 h-96 bg-sky-100/60 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute bottom-10 -left-24 w-80 h-80 bg-amber-100/60 rounded-full blur-3xl pointer-events-none"></div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-2xl mb-14">
            <p className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-sky-600">
              <span className="w-8 h-px bg-sky-600"></span> What we do
            </p>
            <h2 className="mt-3 text-3xl lg:text-5xl font-bold tracking-tight text-slate-900">Everything under one roof</h2>
            <p className="mt-3 text-lg text-slate-600">Money, packages, phones — the essentials your neighborhood needs, done well.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="services-grid">
            <BragTiles />
          </div>
        </div>
      </section>

      {/* NUMBERS / SOCIAL PROOF */}
      <section className="relative py-16 border-y border-slate-200 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white overflow-hidden">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-sky-500/20 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl pointer-events-none"></div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            { value: '500+', label: 'Rotterdam families served', icon: 'fa-users' },
            { value: '100+', label: 'Countries we send to', icon: 'fa-globe' },
            { value: '2016', label: 'Since', icon: 'fa-calendar-check' },
            { value: '9–7', label: 'Mon–Sat · Sun closed', icon: 'fa-clock' },
          ].map((s, i) => (
            <div key={i} className="text-center md:text-left">
              <div className="w-10 h-10 rounded-lg bg-white/10 border border-white/20 flex items-center justify-center mx-auto md:mx-0 mb-3">
                <i className={`fas ${s.icon} text-sky-400`}></i>
              </div>
              <p className="text-4xl lg:text-5xl font-bold tracking-tight text-white">{s.value}</p>
              <p className="mt-1 text-sm text-slate-400">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="py-14 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { icon: Users, label: 'Local Service', hint: 'Trusted by Rotterdam families' },
            { icon: Globe, label: 'Global Reach', hint: 'Send to 100+ countries' },
            { icon: Building2, label: 'Rotterdam-Proud', hint: 'Schiedamseweg since 2016' },
          ].map((t, i) => {
            const I = t.icon;
            return (
              <div key={i} className="group flex items-center gap-4 p-5 rounded-xl bg-slate-50 border border-slate-200 hover:border-sky-300 hover:bg-white hover:shadow-md transition-all">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-sky-500 to-sky-600 flex items-center justify-center shadow-sm group-hover:scale-110 transition-transform">
                  <I size={22} className="text-white" strokeWidth={2} />
                </div>
                <div>
                  <p className="font-bold text-slate-900">{t.label}</p>
                  <p className="text-sm text-slate-500">{t.hint}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-slate-200 py-10 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-sky-500/10 border border-sky-500/20 flex items-center justify-center">
                <i className="fas fa-store text-sky-600"></i>
              </div>
              <span className="font-semibold tracking-tight text-slate-900">Ashop Rotterdam</span>
            </div>
            <p className="mt-4 text-sm text-slate-500 max-w-md">
              Your one-stop shop for money transfer, package pickup, printing, phones, and daily essentials in the heart of Rotterdam.
            </p>
          </div>
          <div className="text-sm text-slate-600 md:text-right">
            <p className="flex items-center gap-2 md:justify-end">
              <MapPin size={16} className="text-sky-600" /> Schiedamseweg 159A, 3026 AK Rotterdam
            </p>
            <p className="mt-2 flex items-center gap-2 md:justify-end">
              <PhoneIcon size={16} className="text-sky-600" /> +31 6 14 35 97 10
            </p>
            <p className="mt-2 flex items-center gap-2 md:justify-end" data-testid="footer-hours">
              <i className="fas fa-clock text-sky-600 text-sm"></i> Mon-Sat 9AM-7PM · Sun closed
            </p>
          </div>
        </div>
        <p className="mt-8 text-center text-xs text-slate-400">© {new Date().getFullYear()} Ashop Rotterdam. All rights reserved.</p>
      </footer>

      {showSuggestion && <SuggestionModal onClose={() => setShowSuggestion(false)} />}
    </div>
  );
};

export default Home;
